import type { Lead } from '@/types/crm'
import { formatDate } from '@/shared/util/dates'
import { STATE_OF, lastActivityAt } from '@/features/dashboards/proCompanyState'
import { linkedCompanyId } from '@/features/dashboards/proLeadMatch'
import type { ProCompaniesState } from '@/features/dashboards/useProCompanies'

/**
 * The lead's Skida PRO company, on the lead — 22 Oct 2026.
 *
 * The other half of the bridge the PRO dashboard links from: where the
 * customer stands in the product, read from the product project with the same
 * state rules the dashboard counts with. Read-only; the link is made and
 * undone on the dashboard, where the evidence for it is.
 */
export function ProCompanyCard({ lead, pro }: { lead: Lead; pro: ProCompaniesState }) {
  const id = linkedCompanyId(lead)

  if (!id) {
    return (
      <section className="card procard">
        <div className="card-label">Skida PRO</div>
        <p className="micro muted">
          Not linked to a Skida PRO company. Companies are linked from the{' '}
          <a href="/pro/dashboard">Skida PRO dashboard</a>, where each one suggests the leads it
          matches by VAT number, email domain or name.
        </p>
      </section>
    )
  }

  const view = pro.byId.get(id)
  if (!view) {
    return (
      <section className="card procard">
        <div className="card-label">Skida PRO</div>
        <p className="micro muted">
          {pro.loading
            ? 'Reading Skida PRO…'
            : pro.error
              ? `Could not read Skida PRO: ${pro.error}`
              : 'The linked Skida PRO company no longer exists in the product.'}
        </p>
      </section>
    )
  }

  const c = view.company
  const st = view.state ? STATE_OF[view.state] : null
  const b = c.billing
  const last = lastActivityAt(c)
  const facts: [string, string][] = [
    ['Guides', String(c.members.length)],
    ['Routes · tours', `${c.routes ?? '—'} · ${c.tours ?? '—'}`],
    ['Paid seats', String(c.members.filter((m) => m.access === 'paid').length)],
    ['Last activity', last ? formatDate(last) : '—'],
    ['In Skida PRO since', c.createdAt ? formatDate(c.createdAt) : '—'],
  ]
  if (b) {
    facts.push(['Legal name', b.companyName || '—'])
    facts.push(['VAT', b.vatNumber ? `${b.vatNumber}${b.vatStatus ? ` (${b.vatStatus})` : ''}` : '—'])
    facts.push(['Billing country', b.countryName || b.countryCode || '—'])
  }

  return (
    <section className="card procard">
      <div className="card-label card-label--row">
        Skida PRO
        <a className="micro" href={`/pro/dashboard?company=${encodeURIComponent(c.id)}`}>
          Open on the PRO dashboard
        </a>
      </div>
      <div className="procard__head">
        <span className="procard__name">{c.name}</span>
        {st && (
          <span className="pill procard__state" style={{ color: st.color }} title={st.hint}>
            {st.label}
          </span>
        )}
        {!b && <span className="micro muted">No billing profile yet</span>}
      </div>
      <dl className="procard__facts">
        {facts.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
